import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Typography, Paper, Button, Box, Container, CircularProgress, Alert, FormHelperText,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import axios from '../utils/api';
import ServiceProviderFormFields from './ServiceProviderFormFields';
import {
  ServiceProviderFormState,
  ServiceProviderValidationErrors,
  emptyServiceProviderForm,
  providerToFormState,
  validateServiceProviderForm,
  buildServiceProviderPayload,
  scrollServiceProviderToFirstError,
} from './serviceProviderShared';

const ServiceProviderEditForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const email = searchParams.get('email') || '';
  const token = searchParams.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState<ServiceProviderValidationErrors>({});
  const [form, setForm] = useState<ServiceProviderFormState>(emptyServiceProviderForm());

  const clearError = (field: keyof ServiceProviderValidationErrors) => {
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  useEffect(() => {
    const load = async () => {
      if (!email || !token) {
        setLoadError('Email and token are required');
        setLoading(false);
        return;
      }
      try {
        const response = await axios.post('/api/service-providers/verify-edit-token', { email, token });
        setForm(providerToFormState(response.data.provider));
      } catch (err: any) {
        setLoadError(err?.response?.data?.message || err?.response?.data?.error || 'Failed to verify token.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [email, token]);

  const handleSubmit = async () => {
    // Registration email is locked; don't require re-validation of it
    const validationErrors = validateServiceProviderForm(form, { requireRegistrantEmail: false });
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      scrollServiceProviderToFirstError(validationErrors);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload = { ...buildServiceProviderPayload(form), email, token };
      await axios.put('/api/service-providers/update-by-token', payload);
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setSubmitError(err?.response?.data?.message || err?.response?.data?.error || 'Failed to update registration.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh', flexDirection: 'column', gap: 2 }}>
        <CircularProgress />
        <Typography>Loading your registration...</Typography>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>{loadError}</Alert>
        <Button variant="outlined" href="/register/agency/edit-request">Request a New Token</Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 } }}>
      <Paper sx={{ p: { xs: 2, sm: 4 }, overflow: 'hidden' }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Edit Your Registration
        </Typography>

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Your registration has been updated successfully.
          </Alert>
        )}

        <form onSubmit={(e) => e.preventDefault()}>
          <ServiceProviderFormFields form={form} setForm={setForm} errors={errors} clearError={clearError} registrantEmailReadOnly />

          {submitError && (
            <Box sx={{ mt: 2 }}>
              <FormHelperText error sx={{ fontSize: '1rem' }}>{submitError}</FormHelperText>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, gap: 2, flexWrap: 'wrap' }}>
            <Button
              onClick={() => navigate(`/register/agency/dashboard?email=${encodeURIComponent(email)}&token=${encodeURIComponent(token)}`)}
            >
              Back to Dashboard
            </Button>
            <Button variant="contained" onClick={handleSubmit} disabled={submitting} startIcon={<Save />} size="large">
              {submitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default ServiceProviderEditForm;
